import { useEffect, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { useDashboard } from '../hooks/useDashboard'
import { SoCIndicator } from '../components/SoCIndicator'
import { PowerCard } from '../components/PowerCard'
import { StatusBadge } from '../components/StatusBadge'
import { RelativeTime } from '../components/RelativeTime'
import { NotLinked } from './NotLinked'
import { LoadingScreen } from './LoadingScreen'
import { Schedule } from './Schedule'
import { ActionChip } from '../components/ActionChip'
import { History } from './History'
import { RecentStrip } from '../components/RecentStrip'

type View = 'live' | 'schedule' | 'history'

/** '#/schedule' (v1.2.0) and '#/history' (v1.3.0) select those pages; anything else the live view. */
function useHashView(): [View, (v: View) => void] {
  const read = (): View =>
    window.location.hash === '#/schedule' ? 'schedule'
      : window.location.hash === '#/history' ? 'history' : 'live'
  const [view, setView] = useState<View>(read)
  useEffect(() => {
    const onHash = () => setView(read())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  const go = (v: View) => { window.location.hash = v === 'live' ? '#/' : `#/${v}` }
  return [view, go]
}

export function Dashboard() {
  const { instance } = useMsal()
  const { result, refresh } = useDashboard()
  const [view, go] = useHashView()

  const signOut = () => {
    instance.logoutRedirect().catch(console.error)
  }

  if (result.state === 'loading') {
    return <LoadingScreen />
  }

  if (result.state === 'not_linked') {
    return (
      <NotLinked
        email={result.me.email}
        onSignOut={signOut}
        onClaimSuccess={refresh}
      />
    )
  }

  if (result.state === 'error') {
    return (
      <div className="error-screen">
        <h1>Something went wrong</h1>
        <p>We couldn't load your system data. This may be a temporary issue.</p>
        <p className="error-detail">
          {result.error.message} (HTTP {result.error.status})
        </p>
        <div className="button-row">
          <button onClick={refresh}>Try again</button>
          <button onClick={signOut} className="btn-secondary">Sign out</button>
        </div>
      </div>
    )
  }

  const { data, lastFetchedAt, me } = result
  const scheduleEnabled = !!data.customer_overrides && data.customer_overrides !== 'DISABLED'
  const historyEnabled = !!data.history_enabled
  const showSchedule = view === 'schedule' && scheduleEnabled
  const showHistory = view === 'history' && historyEnabled
  const showLive = !showSchedule && !showHistory

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="brand">
          <h1>Equivolt Portal</h1>
        </div>
        <div className="user-menu">
          {me.email && <span className="user-email">{me.email}</span>}
          <button onClick={signOut} className="btn-signout">Sign out</button>
        </div>
      </header>

      {(scheduleEnabled || historyEnabled) && (
        <nav className="page-tabs" aria-label="Pages">
          <button className={`page-tab${showLive ? ' active' : ''}`} onClick={() => go('live')}
            aria-current={showLive ? 'page' : undefined}>Live</button>
          {historyEnabled && (
            <button className={`page-tab${showHistory ? ' active' : ''}`} onClick={() => go('history')}
              aria-current={showHistory ? 'page' : undefined}>History</button>
          )}
          {scheduleEnabled && (
            <button className={`page-tab${showSchedule ? ' active' : ''}`} onClick={() => go('schedule')}
              aria-current={showSchedule ? 'page' : undefined}>Battery schedule</button>
          )}
        </nav>
      )}

      {showSchedule ? (
        <main className="dashboard-main">
          <Schedule systemId={data.system_id} />
        </main>
      ) : showHistory ? (
        <main className="dashboard-main">
          <History systemId={data.system_id} />
        </main>
      ) : (
      <main className="dashboard-main">
        {data.is_stale && (
          <div className="stale-warning" role="alert">
            <strong>⚠ Data is out of date.</strong> Your system's most recent reading is
            more than 15 minutes old. This may indicate a connectivity issue.
          </div>
        )}

        <div className="system-info">
          <div className="system-name">
            {data.friendly_name || data.device_id}
          </div>
          <StatusBadge status={data.battery_status} />
        </div>
        {data.action && (
          <div className="system-action"><ActionChip action={data.action} /></div>
        )}

        <section className="grid">
          <SoCIndicator
            socPct={data.soc_pct}
            capacityKwh={data.battery_capacity_kwh}
            energyRemainingKwh={data.energy_remaining_kwh}
          />
          <PowerCard label="Home Load" value={data.load_power_w} unit="W" />
          <PowerCard
            label="Grid"
            value={data.grid_power_w}
            unit="W"
            positiveLabel="importing"
            negativeLabel="exporting"
          />
        </section>

        {historyEnabled && (
          <RecentStrip systemId={data.system_id} onOpenHistory={() => go('history')} />
        )}

        <footer className="dashboard-footer">
          <div className="footer-timing">
            <span>Reading: <RelativeTime timestamp={data.telemetry_timestamp} /></span>
            <span className="separator">·</span>
            <span>Checked: <RelativeTime timestamp={lastFetchedAt} /></span>
          </div>
          <button onClick={refresh} className="btn-refresh">Refresh now</button>
        </footer>
      </main>
      )}
    </div>
  )
}
